import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { Table, Tag, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import type {
  JobChainTemplateSummary,
  JobChainTemplatePage,
  JobChainTemplateVersionSummary,
} from '@/api/JobChainTemplates';
import { useTranslate } from '@/translations';
import { Link, useLocation } from 'react-router-dom';
import { templateListPath } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/editorShared';
import { templateStatusLabel } from '@/modules/SystemAdministration/Jobs/JobChainTemplates/templateLabels';

type TemplateListProps = {
  data: JobChainTemplatePage;
  loading: boolean;
  page: number;
  pageSize: number;
  onPaginationChange: (page: number, pageSize: number) => void;
};

const byVersionDescending = (
  first: JobChainTemplateVersionSummary,
  second: JobChainTemplateVersionSummary,
) => second.version - first.version;

const getOrderedVersions = (
  template: JobChainTemplateSummary,
): JobChainTemplateVersionSummary[] => [
  ...(template.draft ? [template.draft] : []),
  ...(template.active ? [template.active] : []),
  ...[...template.inactiveVersions].sort(byVersionDescending),
];

export const TemplateList: FC<TemplateListProps> = ({
  data,
  loading,
  page,
  pageSize,
  onPaginationChange,
}) => {
  const { t } = useTranslate();
  const { search } = useLocation();

  const formatTimestamp = useCallback(
    (value: string | null): ReactNode => {
      if (!value) return t('jobChainTemplatesNotAvailable');

      return (
        <time dateTime={value}>
          {new Intl.DateTimeFormat('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: 'UTC',
          }).format(new Date(value))}
        </time>
      );
    },
    [t],
  );

  const versionColumns = useMemo<
    TableColumnsType<JobChainTemplateVersionSummary>
  >(
    () => [
      {
        title: t('jobChainTemplatesColumnVersion'),
        dataIndex: 'version',
        render: (version: number, row) => (
          <Link to={`${templateListPath}/${row.id}${search}`}>
            {t('jobChainTemplatesViewVersion', { version })}
          </Link>
        ),
      },
      {
        title: t('jobChainTemplatesColumnStatus'),
        dataIndex: 'status',
        render: (status: JobChainTemplateVersionSummary['status']) => (
          <Tag>{templateStatusLabel(status, t)}</Tag>
        ),
      },
      {
        title: t('jobChainTemplatesColumnCreated'),
        dataIndex: 'createdAt',
        render: (createdAt: string) => formatTimestamp(createdAt),
      },
      {
        title: t('jobChainTemplatesColumnUpdated'),
        dataIndex: 'updatedAt',
        render: (updatedAt: string | null) => formatTimestamp(updatedAt),
      },
    ],
    [formatTimestamp, search, t],
  );

  const templateColumns = useMemo<TableColumnsType<JobChainTemplateSummary>>(
    () => [
      {
        title: t('jobChainTemplatesColumnTemplate'),
        key: 'template',
        render: (_, template) => (
          <div>
            <Typography.Text strong>
              {template.jobChainTypeDisplayName}
            </Typography.Text>
            {template.jobChainTypeDescription ? (
              <div>
                <Typography.Text type="secondary">
                  {template.jobChainTypeDescription}
                </Typography.Text>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: t('jobChainTemplatesColumnScope'),
        key: 'scope',
        render: (_, template) =>
          template.customerName
            ? `${template.scopeDisplayName} · ${template.customerName}`
            : template.scopeDisplayName,
      },
      {
        title: t('jobChainTemplatesColumnActive'),
        key: 'active',
        render: (_, template) =>
          template.active ? (
            <Link to={`${templateListPath}/${template.active.id}${search}`}>
              {t('jobChainTemplatesVersion', {
                version: template.active.version,
              })}
            </Link>
          ) : (
            t('jobChainTemplatesNotAvailable')
          ),
      },
      {
        title: t('jobChainTemplatesColumnDraft'),
        key: 'draft',
        render: (_, template) =>
          template.draft ? (
            <Link to={`${templateListPath}/${template.draft.id}/edit${search}`}>
              {t('jobChainTemplatesVersion', {
                version: template.draft.version,
              })}
            </Link>
          ) : (
            t('jobChainTemplatesNotAvailable')
          ),
      },
      {
        title: t('jobChainTemplatesColumnUsage'),
        key: 'usage',
        render: (_, template) =>
          t('jobChainTemplatesUsage', {
            total: template.totalUsage,
            running: template.runningUsage,
          }),
      },
      {
        title: t('jobChainTemplatesColumnUpdated'),
        dataIndex: 'updatedAt',
        render: (updatedAt: string | null) => formatTimestamp(updatedAt),
      },
    ],
    [formatTimestamp, search, t],
  );

  return (
    <Table<JobChainTemplateSummary>
      columns={templateColumns}
      dataSource={data.items}
      loading={loading}
      rowKey="familyKey"
      expandable={{
        expandedRowRender: (template) => (
          <Table<JobChainTemplateVersionSummary>
            columns={versionColumns}
            dataSource={getOrderedVersions(template)}
            pagination={false}
            rowKey="id"
            size="small"
          />
        ),
        rowExpandable: (template) => getOrderedVersions(template).length > 0,
      }}
      pagination={{
        current: page,
        pageSize,
        total: data.total,
        showSizeChanger: true,
        onChange: onPaginationChange,
      }}
    />
  );
};
